/**
 * PlaylistCard Component
 * 
 * Individual playlist card component that displays:
 * - Playlist thumbnail or placeholder
 * - Title and description
 * - Content type indicator
 * - Item count and estimated duration
 * - Action menu button
 * - Ready status indicator
 */
import React from 'react';
import {
    Card,
    CardContent,
    CardMedia,
    Typography,
    Box,
    Chip,
    IconButton,
    Tooltip
} from '@mui/material';
import {
    MoreVert,
    PlaylistPlay,
    Schedule,
    Visibility,
    VisibilityOff
} from '@mui/icons-material';

const PlaylistCard = ({ playlist, onMenuClick, onCardClick }) => {
    const getContentTypeColor = (contentType) => {
        const colors = {
            'video': 'error',
            'text': 'primary',
            'playlist': 'secondary',
            'mixed': 'warning'
        };
        return colors[contentType] || 'default';
    };

    const getContentTypeLabel = (contentType) => {
        const labels = {
            'video': 'Video Only',
            'text': 'Text Only',
            'playlist': 'Playlists',
            'mixed': 'Mixed'
        };
        return labels[contentType] || contentType;
    };

    const formatDuration = (minutes) => {
        if (minutes < 60) {
            return `${minutes}m`;
        }
        const hours = Math.floor(minutes / 60);
        const remainingMinutes = minutes % 60;
        return `${hours}h ${remainingMinutes}m`;
    };

    const defaultThumbnail = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzIwIiBoZWlnaHQ9IjE4MCIgdmlld0JveD0iMCAwIDMyMCAxODAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIzMjAiIGhlaWdodD0iMTgwIiBmaWxsPSIjRjVGNUY1Ii8+CjxwYXRoIGQ9Ik0xMjggMTEyVjY4TDE2OCA5MUwxMjggMTEyWiIgZmlsbD0iIzlFOUU5RSIvPgo8L3N2Zz4K';

    const handleCardClick = () => {
        if (onCardClick) {
            onCardClick(playlist);
        }
    };

    const handleMenuClick = (e) => {
        e.stopPropagation(); // Prevent card click from firing
        if (onMenuClick) {
            onMenuClick(e, playlist);
        }
    };

    return (
        <Card 
            onClick={handleCardClick}
            sx={{ 
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
                position: 'relative',
                cursor: 'pointer',
                '&:hover': {
                    boxShadow: 3,
                    transform: 'translateY(-2px)'
                },
                transition: 'all 0.2s ease-in-out'
            }}
        >
            {/* Thumbnail */}
            <CardMedia
                component="img"
                height="140"
                image={playlist.thumbUrl || defaultThumbnail}
                alt={playlist.title}
                sx={{ objectFit: 'cover' }}
            />

            {/* Status indicator */}
            <Box
                sx={{
                    position: 'absolute',
                    top: 8,
                    right: 8,
                    display: 'flex',
                    gap: 1
                }}
            >
                <Tooltip title={playlist.isReadyToServe ? 'Ready to serve' : 'Not ready'}>
                    <Chip
                        size="small"
                        icon={playlist.isReadyToServe ? <Visibility /> : <VisibilityOff />}
                        label={playlist.isReadyToServe ? 'Ready' : 'Draft'}
                        color={playlist.isReadyToServe ? 'success' : 'default'}
                        sx={{ backgroundColor: 'rgba(255, 255, 255, 0.9)' }}
                    />
                </Tooltip>
            </Box>

            <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                {/* Header with title and menu */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography 
                        variant="h6" 
                        component="h2" 
                        sx={{ 
                            fontSize: '1rem',
                            lineHeight: 1.2,
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            flexGrow: 1,
                            mr: 1
                        }}
                    >
                        {playlist.title}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={handleMenuClick}
                        sx={{ ml: 1 }}
                    >
                        <MoreVert />
                    </IconButton>
                </Box>

                {/* Content type and stats */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <Chip
                        size="small"
                        label={getContentTypeLabel(playlist.contentType)}
                        color={getContentTypeColor(playlist.contentType)}
                        variant="outlined"
                    />
                </Box>

                {/* Description */}
                {playlist.description && (
                    <Typography 
                        variant="body2" 
                        color="text.secondary" 
                        sx={{ 
                            mb: 2,
                            display: '-webkit-box',
                            overflow: 'hidden',
                            WebkitBoxOrient: 'vertical',
                            WebkitLineClamp: 2,
                            flexGrow: 1
                        }}
                    >
                        {playlist.description}
                    </Typography>
                )}

                {/* Stats */}
                <Box sx={{ mt: 'auto' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <PlaylistPlay fontSize="small" color="action" />
                            <Typography variant="caption" color="text.secondary">
                                {playlist.totalItems || 0} items
                            </Typography>
                        </Box>
                        
                        {playlist.estimatedDuration > 0 && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <Schedule fontSize="small" color="action" />
                                <Typography variant="caption" color="text.secondary">
                                    {formatDuration(playlist.estimatedDuration)}
                                </Typography>
                            </Box>
                        )}
                    </Box>

                    {/* Author and date */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {playlist.author && (
                            <Typography variant="caption" color="text.secondary">
                                by {playlist.author}
                            </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary">
                            {new Date(playlist.createdAt).toLocaleDateString()}
                        </Typography>
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default PlaylistCard;